import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { adminRegistrarDriftService } from "@/services/admin-registrar-drift.service";
import type {
  RegistrarDriftFilters,
  ResolveDomainDriftRequest,
} from "@/types/domain-drift";

/**
 * Query keys for the admin registrar-drift surface.
 *
 *   ['admin', 'registrar-drifts']             — every drift query
 *   ['admin', 'registrar-drifts', 'list']     — list queries
 *   ['admin', 'registrar-drifts', 'list', f]  — a specific filter combo
 */
export const registrarDriftKeys = {
  all: ["admin", "registrar-drifts"] as const,
  lists: () => [...registrarDriftKeys.all, "list"] as const,
  list: (filters: RegistrarDriftFilters) =>
    [...registrarDriftKeys.lists(), filters] as const,
};

/**
 * Paginated drift rows. The dashboard defaults to `resolved=false`.
 */
export function useAdminRegistrarDrifts(filters: RegistrarDriftFilters) {
  return useQuery({
    queryKey: registrarDriftKeys.list(filters),
    queryFn: () => adminRegistrarDriftService.list(filters),
    staleTime: 30_000,
    gcTime: 5 * 60_000,
  });
}

/**
 * Resolve one drift row. Invalidates every drift query on success so the
 * panel reflects the updated state immediately.
 */
export function useResolveRegistrarDrift() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: ResolveDomainDriftRequest;
    }) => adminRegistrarDriftService.resolve(id, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: registrarDriftKeys.all });
      toast.success("Drift resolved");
    },
    onError: (error: Error) => {
      toast.error("Failed to resolve drift", { description: error.message });
    },
  });
}
